/**
 * Structural edits to a questionnaire: delete a question, splice one in.
 *
 * Pure functions over a definition, separated from the reducer so the IMPACT of an
 * edit can be computed and shown BEFORE it is applied. That ordering is the whole
 * point: deleting a question in this data model has three non-obvious consequences,
 * and a confirm dialog that does not name them is not a confirmation.
 *
 *  1. Every rule reading the deleted question's `qualifiedNodeId` keeps its
 *     expression and silently stops matching. The engine does not error — it reads
 *     `undefined` out of the metric map and the rule is simply never true again.
 *  2. Predecessors have to be re-pointed somewhere, and "somewhere" is a choice.
 *  3. Anything only reachable THROUGH the deleted node becomes unreachable, so a
 *     one-node delete can silently strand a whole sub-tree.
 */
import type {
  AnswerOption,
  ExpressionRule,
  QuestionnaireDefinition,
  QuestionnaireNode,
} from '../types';
import { entryNodeOf, metricRefsOf } from './projection';

/** A rule that will stop matching because the answer it reads is gone. */
export interface BrokenRule {
  /** Node the rule hangs off. */
  nodeId: string;
  kind: '条件边' | '资格规则' | '自动作答';
  expression: string;
}

export interface DeleteImpact {
  /** Set when the delete must be refused outright. */
  blockedReason?: string;
  /** Where predecessors will be re-pointed. undefined = they lose their edge. */
  relinkTo?: string;
  /** Nodes that become unreachable and are removed along with the target. */
  orphans: string[];
  /** Rules elsewhere that read this question and will never match again. */
  brokenRules: BrokenRule[];
}

/** Successor used when re-pointing predecessors: the unconditional edge, else the first rule's. */
function primarySuccessorOf(n: QuestionnaireNode): string | undefined {
  if (n.isLastNode) return undefined;
  if (n.nextNodeId) return n.nextNodeId;
  return n.nextQuestionRules?.[0]?.nextQuestionId;
}

/** Rules anywhere in the questionnaire that read `qualifiedId`. */
export function rulesReading(
  definition: QuestionnaireDefinition,
  qualifiedId: string | undefined,
): BrokenRule[] {
  if (!qualifiedId) return [];
  const out: BrokenRule[] = [];
  for (const n of definition.nodes) {
    const scan = (kind: BrokenRule['kind'], expression: string) => {
      if (metricRefsOf(expression).includes(qualifiedId)) {
        out.push({ nodeId: n.nodeId, kind, expression });
      }
    };
    (n.nextQuestionRules ?? []).forEach((r) => scan('条件边', r.expression));
    (n.programRules ?? []).forEach((r) => scan('资格规则', r.expression));
    (n.autoAnswerRules ?? []).forEach((r) => scan('自动作答', r.expression));
  }
  return out;
}

/** Forward adjacency, honouring the engine's `nextNodeId`-wins precedence. */
function adjacencyOf(nodes: QuestionnaireNode[]): Map<string, string[]> {
  const ids = new Set(nodes.map((n) => n.nodeId));
  const adj = new Map<string, string[]>();
  for (const n of nodes) {
    const targets = n.isLastNode
      ? []
      : n.nextNodeId
        ? [n.nextNodeId]
        : (n.nextQuestionRules ?? []).map((r) => r.nextQuestionId);
    adj.set(
      n.nodeId,
      targets.filter((t) => ids.has(t)),
    );
  }
  return adj;
}

function reachable(entry: string | undefined, adj: Map<string, string[]>): Set<string> {
  const seen = new Set<string>();
  if (!entry) return seen;
  const stack = [entry];
  while (stack.length) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const n of adj.get(cur) ?? []) if (!seen.has(n)) stack.push(n);
  }
  return seen;
}

/** Re-point every edge aimed at `from` to `to` (or drop it when `to` is undefined). */
function repoint(nodes: QuestionnaireNode[], from: string, to: string | undefined): QuestionnaireNode[] {
  return nodes.map((n) => {
    let next: QuestionnaireNode = n;
    if (n.nextNodeId === from) {
      const copy = { ...next } as Record<string, unknown>;
      if (to) copy.nextNodeId = to;
      else delete copy.nextNodeId;
      next = copy as unknown as QuestionnaireNode;
    }
    if (n.nextQuestionRules?.some((r) => r.nextQuestionId === from)) {
      const rules = n
        .nextQuestionRules!.map((r): ExpressionRule | null =>
          r.nextQuestionId === from ? (to ? { ...r, nextQuestionId: to } : null) : r,
        )
        .filter((r): r is ExpressionRule => r !== null);
      next = { ...next, nextQuestionRules: rules };
    }
    return next;
  });
}

/**
 * What deleting `nodeId` would do. Never mutates.
 *
 * The entry node is refused rather than handled: re-pointing "the thing before the
 * start" is meaningless, and picking a new entry is a different, deliberate decision
 * that should not be a side effect of a delete.
 */
export function simulateDelete(
  definition: QuestionnaireDefinition,
  nodeId: string,
): DeleteImpact {
  const target = definition.nodes.find((n) => n.nodeId === nodeId);
  if (!target) return { blockedReason: '节点不存在', orphans: [], brokenRules: [] };

  const entry = entryNodeOf(definition.nodes);
  if (entry?.nodeId === nodeId) {
    return {
      blockedReason:
        '这是入口节点，不能删除 —— 删掉它整份问卷就没有起点了。要换入口，请先把 isFirstNode 移到别的节点上。',
      orphans: [],
      brokenRules: [],
    };
  }

  const relinkTo = primarySuccessorOf(target);
  const brokenRules = rulesReading(definition, target.qualifiedNodeId).filter(
    (r) => r.nodeId !== nodeId,
  );

  const afterNodes = repoint(
    definition.nodes.filter((n) => n.nodeId !== nodeId),
    nodeId,
    relinkTo,
  );
  const stillReachable = reachable(entry?.nodeId, adjacencyOf(afterNodes));
  const orphans = afterNodes.map((n) => n.nodeId).filter((id) => !stillReachable.has(id));

  return { relinkTo, orphans, brokenRules };
}

/**
 * Apply the delete simulated above: drop the node, re-point its predecessors, and
 * drop whatever became unreachable.
 *
 * Orphans go too, rather than being left behind as dead nodes. A questionnaire with
 * an unreachable sub-tree is a worse outcome than a smaller one: the nodes still show
 * up in every list and rule picker, and nothing will ever ask them.
 */
export function applyDelete(
  definition: QuestionnaireDefinition,
  nodeId: string,
): QuestionnaireDefinition {
  const impact = simulateDelete(definition, nodeId);
  if (impact.blockedReason) return definition;
  const drop = new Set([nodeId, ...impact.orphans]);
  let nodes = definition.nodes.filter((n) => !drop.has(n.nodeId));
  nodes = repoint(nodes, nodeId, impact.relinkTo);
  // Orphans are gone, so any edge still aimed at one has to go as well.
  for (const o of impact.orphans) nodes = repoint(nodes, o, undefined);
  return { ...definition, nodes };
}

/** Whether a new branch can be hung off `nodeId`, and why not when it cannot. */
export function canAddBranch(
  definition: QuestionnaireDefinition,
  nodeId: string,
): { ok: boolean; reason?: string } {
  const n = definition.nodes.find((x) => x.nodeId === nodeId);
  if (!n) return { ok: false, reason: '节点不存在' };
  if (n.isLastNode) {
    return {
      ok: false,
      reason: '终点节点没有出边 —— 先取消「终点节点」勾选，再加分支。',
    };
  }
  return { ok: true };
}

/**
 * Answer values of `nodeId` that no existing conditional edge mentions.
 *
 * Offered as the condition for a new branch, which is the common case: a yes/no
 * question with only one side wired up. Matching on the raw value inside the
 * expression text is deliberately crude — it is a suggestion for a dropdown, not a
 * correctness claim.
 */
export function unusedOptionValues(
  definition: QuestionnaireDefinition,
  nodeId: string,
): string[] {
  const n = definition.nodes.find((x) => x.nodeId === nodeId);
  if (!n?.answerOptions?.length) return [];
  const wired = (n.nextQuestionRules ?? []).map((r) => r.expression).join(' ');
  return n.answerOptions.map((o) => o.value).filter((v) => !wired.includes(`'${v}'`));
}

/**
 * The expression a branch on `value` should carry.
 *
 * A multi-select answer is stored as an ARRAY, so it is matched with `.includes()`,
 * never `===` — the live `Q1.1.3.1` rules do exactly this. Emitting `===` for a
 * multi-select node produced a branch that can never fire: no error, no warning, just
 * a path the questionnaire never takes, which is the precise failure this surface
 * exists to prevent.
 */
export function branchExpressionFor(
  definition: QuestionnaireDefinition,
  nodeId: string,
  value: string,
): string {
  const n = definition.nodes.find((x) => x.nodeId === nodeId);
  const qid = n?.qualifiedNodeId ?? nodeId;
  return n?.multiSelect
    ? `metric['${qid}'].includes('${value}')`
    : `metric['${qid}'] === '${value}'`;
}

/** First-match-wins means a `true` rule is a default; recognise it so we never bury one. */
const isCatchAll = (expression: string) => expression.trim() === 'true';

/** A fresh question: terminal, two options, no rules. */
/**
 * A new node of the requested kind, with defaults that are already VALID for it.
 *
 * Every new node used to be a single-select with 是/否, so authoring a numeric question
 * meant three separate corrections in the inspector — change `questionType`, empty the
 * options, set `dataType: NUMBER` — and forgetting the third leaves a TEXT node whose
 * rules do arithmetic, which is one of the two silent breakages the checker exists to
 * catch. Minting it right is cheaper than catching it later.
 *
 * `NewNodeKind` is wider than `QuestionKind` on purpose: an amount and a percentage
 * are both `number`, differing only in `unit` (and `scale`, for the percentage, which
 * changes the stored value). They are offered as separate CHOICES because picking
 * "percentage" and getting the ratio semantics right is exactly what an author should
 * not have to remember — but they do not become separate kinds.
 */
export type NewNodeKind = 'single' | 'multi' | 'number' | 'amount' | 'percent' | 'text' | 'sfdc';

function freshNode(
  definition: QuestionnaireDefinition,
  kind: NewNodeKind = 'single',
): QuestionnaireNode {
  const { nodeId, qualifiedNodeId } = freshIds(definition);
  const base = {
    nodeId,
    qualifiedNodeId,
    questionText: { id: '', en: 'New question', cn: '新问题' },
    // Terminal while it is being filled in, so it does not trip the dead-end error
    // (a non-terminal node with no out-edge). Wiring an edge out of it clears this.
    isLastNode: true,
    // The FIRST node of an empty questionnaire is the entry, explicitly. `entryNodeOf`
    // would fall back to array order anyway, but then deleting the first node moves the
    // entry silently — and every live `q_1_*.json` sets the flag.
    ...(definition.nodes.length === 0 ? { isFirstNode: true } : {}),
  };
  const yesNo = [
    { optionId: `${nodeId}_a`, optionText: { id: '', en: 'Yes', cn: '是' }, value: 'y' },
    { optionId: `${nodeId}_b`, optionText: { id: '', en: 'No', cn: '否' }, value: 'n' },
  ];
  switch (kind) {
    case 'multi':
      return { ...base, questionType: 'LIST_SELECTION', multiSelect: true, answerOptions: yesNo };
    case 'number':
      // `dataType: NUMBER` is the whole point of choosing this kind — without it every
      // comparison in a rule degrades to a string compare.
      return { ...base, questionType: 'INPUT_NUMBER', dataType: 'NUMBER', answerOptions: [] };
    case 'amount':
      // Just a number with a label. No `scale`: `$K` does not change the stored value.
      return {
        ...base,
        questionType: 'INPUT_NUMBER',
        dataType: 'NUMBER',
        answerOptions: [],
        unit: '$K',
      };
    case 'percent':
      /*
       * `unit` and `scale` together, never apart. The author types 30, the engine sees
       * 0.3, and a rule has to say `>= 0.3` — offering a `%` label without the scale
       * would tell the rule author the wrong magnitude, which is the live
       * `Q_1_1_10` / `Q_1_2_6` trap.
       */
      return {
        ...base,
        questionType: 'INPUT_NUMBER',
        dataType: 'NUMBER',
        answerOptions: [],
        unit: '%',
        scale: 0.01,
      };
    case 'sfdc':
      /*
       * Its own kind, not a text box: the component owns a remote data source
       * (`gcr-company-service-client`), two modes, and remote validation. `INPUT_TEXT`
       * is the fallback shape for a client that does not know the component.
       */
      return {
        ...base,
        questionType: 'INPUT_TEXT',
        answerOptions: [],
        uiComponent: 'sfdc-account',
      };
    case 'text':
      return { ...base, questionType: 'INPUT_TEXT', answerOptions: [] };
    default:
      return { ...base, questionType: 'LIST_SELECTION', answerOptions: yesNo };
  }
}

/**
 * Add one outgoing rule to a node, preserving first-match-wins semantics.
 *
 * Shared by {@link addBranch} and {@link connectNodes}: both need "give this node one
 * more conditional edge", and both have to handle the same two traps — an existing
 * unconditional `nextNodeId` that would shadow every rule, and an existing `true`
 * catch-all that would shadow anything appended after it.
 */
function withExtraRule(
  from: QuestionnaireNode,
  expression: string,
  targetNodeId: string,
): QuestionnaireNode {
  const existing: ExpressionRule[] = from.nextNodeId
    ? [{ expression: 'true', nextQuestionId: from.nextNodeId }]
    : [...(from.nextQuestionRules ?? [])];

  const catchAllAt = existing.findIndex((r) => isCatchAll(r.expression));
  const newRule: ExpressionRule = { expression, nextQuestionId: targetNodeId };
  const rules =
    catchAllAt < 0
      ? [...existing, newRule]
      : [...existing.slice(0, catchAllAt), newRule, ...existing.slice(catchAllAt)];

  const patched = { ...from, nextQuestionRules: rules, isLastNode: false } as Record<
    string,
    unknown
  >;
  // Must go, or the conversion is pointless: the engine would still take the static
  // edge and ignore every rule we just wrote.
  delete patched.nextNodeId;
  return patched as unknown as QuestionnaireNode;
}

/**
 * A detached new question, with no edges at all.
 *
 * Deliberately unreachable: the checker reports it as such, which is the correct
 * reading of "a card sitting on the canvas that nothing leads to yet". The mouse-first
 * flow is create → configure → drag a connection in, and the warning is what tells
 * you the third step has not happened.
 */
export function addDetachedNode(
  definition: QuestionnaireDefinition,
  kind: NewNodeKind = 'single',
): QuestionnaireDefinition {
  return { ...definition, nodes: [...definition.nodes, freshNode(definition, kind)] };
}

/** What wiring an edge out of `sourceNodeId` will take. */
export type ConnectionKind =
  /** First outgoing edge — unconditional, nothing to ask. */
  | { kind: 'unconditional' }
  /** The node already has an out-edge, so the new one needs a condition. */
  | { kind: 'needs-condition'; convertsStaticEdge: boolean }
  | { kind: 'blocked'; reason: string };

export function connectionKind(
  definition: QuestionnaireDefinition,
  sourceNodeId: string,
  targetNodeId?: string,
): ConnectionKind {
  const n = definition.nodes.find((x) => x.nodeId === sourceNodeId);
  if (!n) return { kind: 'blocked', reason: '源节点不存在' };
  if (targetNodeId === sourceNodeId) {
    return { kind: 'blocked', reason: '不能连到自己 —— 那是一个长度为 1 的环。' };
  }
  if (targetNodeId && !definition.nodes.some((x) => x.nodeId === targetNodeId)) {
    return { kind: 'blocked', reason: '目标节点不存在' };
  }
  const hasOut = !!n.nextNodeId || (n.nextQuestionRules?.length ?? 0) > 0;
  if (!hasOut) return { kind: 'unconditional' };
  if (targetNodeId) {
    const already =
      n.nextNodeId === targetNodeId ||
      (n.nextQuestionRules ?? []).some((r) => r.nextQuestionId === targetNodeId);
    // Two edges to the same target are legal but almost always a mis-drag, and the
    // second one can never be distinguished in the graph.
    if (already) {
      return { kind: 'blocked', reason: `已经有一条连到「${targetNodeId}」的边了。` };
    }
  }
  return { kind: 'needs-condition', convertsStaticEdge: !!n.nextNodeId };
}

/**
 * Wire `source → target`, the mouse-drawn equivalent of editing `nextNodeId` /
 * `nextQuestionRules` by hand.
 *
 * The first edge out of a node is unconditional, which is what makes the common case
 * (drag a line, done) need no dialog. Any further edge needs a condition, because
 * two unconditional edges cannot both be taken — the engine would silently follow one.
 *
 * `isLastNode` is cleared either way: dragging a connection OUT of a node is an
 * unambiguous statement that it is not the end of the walk. Making the user go untick
 * a checkbox first would be friction with no decision behind it.
 */
export function connectNodes(
  definition: QuestionnaireDefinition,
  sourceNodeId: string,
  targetNodeId: string,
  expression?: string,
): QuestionnaireDefinition {
  const k = connectionKind(definition, sourceNodeId, targetNodeId);
  if (k.kind === 'blocked') return definition;
  if (k.kind === 'needs-condition' && !expression?.trim()) return definition;

  const idx = definition.nodes.findIndex((n) => n.nodeId === sourceNodeId);
  const from = definition.nodes[idx];
  const nodes = [...definition.nodes];
  nodes[idx] =
    k.kind === 'unconditional'
      ? ({ ...from, nextNodeId: targetNodeId, isLastNode: false } as QuestionnaireNode)
      : withExtraRule(from, expression!.trim(), targetNodeId);
  return { ...definition, nodes };
}

/**
 * Remove the edge `source → target`.
 *
 * A node left with no outgoing edge is NOT auto-marked terminal: that would guess at
 * intent in the middle of a re-wire. The checker reports the dead end instead, which
 * is the honest state — "you took the line out and have not put one back".
 */
export function disconnectEdge(
  definition: QuestionnaireDefinition,
  sourceNodeId: string,
  targetNodeId: string,
  ruleIndex?: number,
): QuestionnaireDefinition {
  const idx = definition.nodes.findIndex((n) => n.nodeId === sourceNodeId);
  if (idx < 0) return definition;
  const from = definition.nodes[idx];

  let next: QuestionnaireNode | null = null;
  if (from.nextNodeId === targetNodeId) {
    const copy = { ...from } as Record<string, unknown>;
    delete copy.nextNodeId;
    next = copy as unknown as QuestionnaireNode;
  } else if (from.nextQuestionRules?.length) {
    // ONE rule, not every rule aimed at this target. Two conditions may legitimately
    // route to the same question — different answers, same next step — and the
    // projection draws one edge per rule, so filtering by target alone made a single
    // click on one of those lines silently delete its twin as well. `insertOnEdge`
    // already diverts just one for the same reason.
    //
    // `ruleIndex` identifies WHICH, when the caller knows (both do: `CpaEdge` carries
    // it). Without it, the first match is removed — enough that one click removes one
    // edge rather than all of them.
    const at =
      ruleIndex !== undefined &&
      from.nextQuestionRules[ruleIndex]?.nextQuestionId === targetNodeId
        ? ruleIndex
        : from.nextQuestionRules.findIndex((r) => r.nextQuestionId === targetNodeId);
    if (at >= 0) {
      next = {
        ...from,
        nextQuestionRules: from.nextQuestionRules.filter((_, i) => i !== at),
      };
    }
  }
  if (!next) return definition;

  const nodes = [...definition.nodes];
  nodes[idx] = next;
  return { ...definition, nodes };
}

/**
 * Hang a NEW question off `fromNodeId`, reached when `expression` is true.
 *
 * Two cases, and the first one is why this is not just "push a rule":
 *
 *  - The node currently has an unconditional `nextNodeId`. It cannot keep that AND
 *    have a second branch: QuestionnaireGenerator.enhance() installs the static
 *    calculator the moment `nextNodeId` is set and never evaluates the rules. So the
 *    static edge is CONVERTED into a trailing `true` rule. Because rules are
 *    first-match-wins, a trailing `true` is a default branch — the old behaviour is
 *    preserved exactly for every answer that does not match the new condition.
 *  - The node already has conditional edges. The new rule goes in BEFORE any existing
 *    `true` catch-all, otherwise it would sit behind the default and never fire.
 */
export function addBranch(
  definition: QuestionnaireDefinition,
  fromNodeId: string,
  expression: string,
): QuestionnaireDefinition {
  const idx = definition.nodes.findIndex((n) => n.nodeId === fromNodeId);
  if (idx < 0) return definition;
  if (!canAddBranch(definition, fromNodeId).ok) return definition;

  const fresh = freshNode(definition);
  const nodes = [...definition.nodes];
  nodes[idx] = withExtraRule(definition.nodes[idx], expression, fresh.nodeId);
  nodes.splice(idx + 1, 0, fresh);
  return { ...definition, nodes };
}

/**
 * Set — or remove — the condition on an existing edge.
 *
 * This is what makes the LINE the place a relation is configured. Previously the
 * condition could only be read on the edge and the unconditional target could only be
 * picked from a dropdown on the source CARD, which split one relation across two
 * places and let the card imply it owned something it does not: an edge is a fact
 * about two nodes, not a property of either.
 *
 * `expression === null` means "make this the unconditional edge", and it is refused
 * unless it is the node's ONLY outgoing edge — the engine takes `nextNodeId` and stops
 * looking at rules, so an unconditional edge beside conditional ones would silently
 * disable all of them.
 */
export function setEdgeCondition(
  definition: QuestionnaireDefinition,
  sourceNodeId: string,
  targetNodeId: string,
  expression: string | null,
): QuestionnaireDefinition {
  const idx = definition.nodes.findIndex((n) => n.nodeId === sourceNodeId);
  if (idx < 0) return definition;
  const from = definition.nodes[idx];
  const rules = from.nextQuestionRules ?? [];
  const hasStatic = from.nextNodeId === targetNodeId;
  const ruleAt = rules.findIndex((r) => r.nextQuestionId === targetNodeId);
  if (!hasStatic && ruleAt < 0) return definition;

  const nodes = [...definition.nodes];

  if (expression === null) {
    const outCount = (from.nextNodeId ? 1 : 0) + rules.length;
    if (outCount > 1) return definition;
    const patched = { ...from, nextNodeId: targetNodeId } as Record<string, unknown>;
    delete patched.nextQuestionRules;
    nodes[idx] = patched as unknown as QuestionnaireNode;
    return { ...definition, nodes };
  }

  const expr = expression.trim();
  if (!expr) return definition;

  if (hasStatic) {
    // Becoming conditional: the static edge turns into the single rule. No `true`
    // default is invented here — the caller asked for this specific condition, and
    // adding a silent catch-all would change behaviour without being asked.
    const patched = {
      ...from,
      nextQuestionRules: [{ expression: expr, nextQuestionId: targetNodeId }],
    } as Record<string, unknown>;
    delete patched.nextNodeId;
    nodes[idx] = patched as unknown as QuestionnaireNode;
    return { ...definition, nodes };
  }

  if (rules[ruleAt].expression === expr) return definition;
  nodes[idx] = {
    ...from,
    nextQuestionRules: rules.map((r, i) => (i === ruleAt ? { ...r, expression: expr } : r)),
  };
  return { ...definition, nodes };
}

/**
 * Move one conditional edge up or down in its node's evaluation order.
 *
 * Order is load-bearing and, until this existed, un-editable: `nextQuestionRules` is
 * first-match-wins, so a rule's position decides whether it can fire at all. The two
 * ways to produce a dead branch — a catch-all ahead of real conditions, or a duplicate
 * condition behind its twin — are both reachable purely by reordering, which is why
 * this does NOT refuse to create them: the consistency panel reports them, and being
 * able to see a bad order is more useful than being prevented from expressing it.
 *
 * Returns the definition unchanged when the move is a no-op or out of range.
 */
export function moveRule(
  definition: QuestionnaireDefinition,
  nodeId: string,
  from: number,
  to: number,
): QuestionnaireDefinition {
  const idx = definition.nodes.findIndex((n) => n.nodeId === nodeId);
  if (idx < 0) return definition;
  const rules = definition.nodes[idx].nextQuestionRules;
  if (!rules || from === to) return definition;
  if (from < 0 || from >= rules.length || to < 0 || to >= rules.length) return definition;
  const next = [...rules];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  const nodes = [...definition.nodes];
  nodes[idx] = { ...nodes[idx], nextQuestionRules: next };
  return { ...definition, nodes };
}

/** Next free `NEW_n` id pair, so two inserts in a row do not collide. */
function freshIds(definition: QuestionnaireDefinition): { nodeId: string; qualifiedNodeId: string } {
  const taken = new Set(definition.nodes.map((n) => n.nodeId));
  let i = 1;
  while (taken.has(`NEW_${i}`)) i++;
  return { nodeId: `NEW_${i}`, qualifiedNodeId: `NEW_${i}` };
}

/**
 * Splice a new question onto the edge `source → target`.
 *
 * Inserting ON AN EDGE rather than "add a floating node" is the difference between an
 * edit that leaves the questionnaire walkable and one that leaves an unreachable box
 * for someone to wire up later. The new node inherits the edge: `source` now points at
 * it, and it points at `target`.
 *
 * The node is created with two yes/no options and NO rules, which means it is asked but
 * changes nothing until a rule reads it — reported by the checker as
 * "没有任何规则读取这个问题的答案".
 */
export function insertOnEdge(
  definition: QuestionnaireDefinition,
  sourceNodeId: string,
  targetNodeId: string,
): QuestionnaireDefinition {
  const srcIdx = definition.nodes.findIndex((n) => n.nodeId === sourceNodeId);
  if (srcIdx < 0) return definition;
  const { nodeId, qualifiedNodeId } = freshIds(definition);

  const options: AnswerOption[] = [
    { optionId: `${nodeId}_a`, optionText: { id: '', en: 'Yes', cn: '是' }, value: 'y' },
    { optionId: `${nodeId}_b`, optionText: { id: '', en: 'No', cn: '否' }, value: 'n' },
  ];
  const fresh: QuestionnaireNode = {
    nodeId,
    qualifiedNodeId,
    questionText: { id: '', en: 'New question', cn: '新问题' },
    questionType: 'LIST_SELECTION',
    answerOptions: options,
    isLastNode: false,
    nextNodeId: targetNodeId,
  };

  // Re-point only the ONE edge being split. A source with several conditional edges
  // to the same target would otherwise have all of them diverted.
  const src = definition.nodes[srcIdx];
  let patched: QuestionnaireNode = src;
  if (src.nextNodeId === targetNodeId) {
    patched = { ...src, nextNodeId: nodeId };
  } else if (src.nextQuestionRules?.length) {
    let done = false;
    patched = {
      ...src,
      nextQuestionRules: src.nextQuestionRules.map((r) => {
        if (!done && r.nextQuestionId === targetNodeId) {
          done = true;
          return { ...r, nextQuestionId: nodeId };
        }
        return r;
      }),
    };
  }

  const nodes = [...definition.nodes];
  nodes[srcIdx] = patched;
  // Inserted right after its source so array order still tracks walk order — the
  // projection breaks rank ties on array index, so this keeps the new node in the
  // column its position implies.
  nodes.splice(srcIdx + 1, 0, fresh);
  return { ...definition, nodes };
}
